import type { Signal } from '@angular/core'
import { inject } from '@angular/core'
import { tapResponse } from '@ngrx/operators'
import { signalStoreFeature, type, withMethods, withState } from '@ngrx/signals'
import { Events, injectDispatch, on, withEventHandlers, withReducer } from '@ngrx/signals/events'
import { exhaustMap, filter, map, takeUntil, tap } from 'rxjs'

import { isPaymentRequired } from '@app/core/is-payment-required'
import { nonCancelableRequest } from '@app/core/rxjs/non-cancelable-request'
import { CreditsStore } from '@app/modules/credits/store/credits.store'
import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import type { ConversationState } from '@app/modules/messaging/components/store/conversation.store'
import { CONVERSATION_STORE_CONFIG } from '@app/modules/messaging/components/store/conversation.store'
import { injectDefaultErrorHandler } from '@app/modules/messaging/components/store/default-error-handler'
import type { Conversation } from '@app/modules/messaging/models/conversation.model'
import type { Message } from '@app/modules/messaging/models/message.model'
import { isFakeMessage } from '@app/modules/messaging/models/message.model'
import { ConversationUIService } from '@app/modules/messaging/services/conversation-ui.service'
import { ConversationService } from '@app/modules/messaging/services/conversation.service'
import { MessagingService } from '@app/modules/messaging/services/messaging.service'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'

import { readReceiptsEvents } from './read-receipts.events'
import { boostEvents } from '../../boost/store/boost.events'
import type { ConversationReadReceiptsUnlockType } from '../models/conversation-read-receipts-unlock-type.model'

interface State {
  unlockReadReceiptsInProgress: boolean
}

const initialState: State = {
  unlockReadReceiptsInProgress: false,
}

export const readReceiptsFeature = () =>
  signalStoreFeature(
    { state: type<ConversationState>(), props: type<{ messages: Signal<Message[]> }>() },
    withState(initialState),
    withMethods(
      (
        _store,
        source = inject(CONVERSATION_STORE_CONFIG).source,
        creditsNavigationService = inject(CreditsNavigationService),
        dispatch = injectDispatch(readReceiptsEvents),
        messagingService = inject(MessagingService),
      ) => ({
        attemptToUnlockReadReceipts: (
          message: Message,
          conversation: Conversation,
          unlockType: ConversationReadReceiptsUnlockType,
        ) => {
          if (!messagingService.canAffordConversationReadReceiptsUnlock()) {
            creditsNavigationService.visitCredits('unlockReadReceipts', {
              source,
              profile_id: conversation.profile.id,
            })
            return
          }
          dispatch.unlockReadReceipts({ conversation, unlockType, message })
        },
      }),
    ),
    withReducer(
      on(readReceiptsEvents.unlockReadReceipts, () => ({ unlockReadReceiptsInProgress: true })),
      on(readReceiptsEvents.unlockReadReceiptsEnd, () => ({ unlockReadReceiptsInProgress: false })),
    ),
    withEventHandlers(
      (
        store,
        source = inject(CONVERSATION_STORE_CONFIG).source,
        conversationService = inject(ConversationService),
        creditsNavigationService = inject(CreditsNavigationService),
        creditsStore = inject(CreditsStore),
        dispatch = injectDispatch(readReceiptsEvents),
        defaultErrorHandler = injectDefaultErrorHandler(),
        events = inject(Events),
        conversationUIService = inject(ConversationUIService),
      ) => ({
        onUnlockReadReceipts$: events.on(readReceiptsEvents.unlockReadReceipts).pipe(
          map(({ payload }) => payload),
          exhaustMap(({ conversation, unlockType, message }) =>
            conversationService.unlockReadReceipts(conversation.profile.browse_id, unlockType, source).pipe(
              tap(response => creditsStore.onCreditsSpent(response)),
              nonCancelableRequest(),
              tapResponse({
                next: response =>
                  dispatch.unlockReadReceiptsSuccess({
                    response,
                    message: store
                      .messages()
                      .find(({ timestamp, fe_message_id }) =>
                        isFakeMessage(message)
                          ? message.fe_message_id === fe_message_id
                          : message.timestamp === timestamp,
                      )!,
                    conversation: store.conversation()!,
                  }),
                error: error => dispatch.unlockReadReceiptsFailure({ error, profile_id: conversation.profile.id }),
                finalize: () => dispatch.unlockReadReceiptsEnd(),
              }),
              takeUntil(events.on(conversationEvents.select)),
            ),
          ),
        ),

        onUnlockReadReceiptsFailure$: events.on(readReceiptsEvents.unlockReadReceiptsFailure).pipe(
          map(({ payload }) => payload),
          tap(({ error, profile_id }) => {
            if (isPaymentRequired(error)) {
              return creditsNavigationService.visitCredits('unlockReadReceipts', { source, profile_id })
            }

            defaultErrorHandler(error)
          }),
        ),

        openKnowWhenTheyReadItModalOnBoostMessageSuccess$: events.on(boostEvents.boostSuccess).pipe(
          map(({ payload }) => payload),
          filter(({ conversation }) => conversation.read_receipts_locked && !store.unlockReadReceiptsInProgress()),
          tap(({ message, conversation }) => {
            const instance = conversationUIService.openKnowWhenTheyReadItModal()
            instance.onConfirm(() => store.attemptToUnlockReadReceipts(message, conversation, 'post-boost-modal'))
          }),
        ),
      }),
    ),
  )
